import { Text } from '../../primitives/Text';
import { View } from '../../primitives/View';
import { Circle, Svg } from '../../svg';
import { useTheme } from '../../theme';
import { cn } from '../../utils/cn';
import { subTestID } from '../../utils/testId';
import { AnimatedFill } from './AnimatedFill';
import {
  CIRCULAR_GEOMETRY_FOR,
  resolveProgressRingColors,
  SEMICIRCLE_SWEEP_DEG,
} from './ProgressBar.colors';
import type { ProgressBarProps } from './ProgressBar.types';
import {
  isProgressSegmentDimmed,
  PROGRESS_BAR_LABEL_CLASS,
  progressCircularLabelVariants,
  progressFillVariants,
  progressSegmentVariants,
  progressTrackVariants,
} from './ProgressBar.variants';

/**
 * Cross-platform ProgressBar — linear, segmented, or circular progress.
 * Renders identically on web and React Native. Figma: "Progress bar"
 * (light `2304:9843` · dark `4890:33310`); dark mode comes from the
 * auto-inverting palette (classes) + explicit hex mirroring (the SVG ring).
 *
 * @example
 * ```tsx
 * import { ProgressBar } from '@flashka/shared/ui';
 *
 * // Linear (default)
 * <ProgressBar value={30} showLabel />
 *
 * // A running task (upload, processing): smooth, never backwards, sheen while
 * // in progress, fast finish at 100. Start indeterminate until the size is known.
 * <ProgressBar live indeterminate={!total} value={total ? (done / total) * 100 : 0} />
 *
 * // Segmented — 3 of 4 steps done
 * <ProgressBar type="segmented" colour="green" segments={4} value={75} />
 *
 * // Segmented with step 2 highlighted — every other segment dims
 * <ProgressBar type="segmented" segments={5} value={80} highlightedSegment={1} />
 *
 * // Circular ring with the label inside
 * <ProgressBar type="circular" size="large" value={30} showLabel />
 *
 * // Semicircle gauge — open at the bottom, label centred inside
 * <ProgressBar type="semicircle" size="xlarge" value={30} showLabel />
 * ```
 */
export function ProgressBar({
  type = 'linear',
  colour: colourProp,
  size: sizeProp,
  value = 0,
  segments = 4,
  highlightedSegment,
  showLabel = false,
  indeterminate = false,
  live = false,
  sheen,
  className,
  accessibilityLabel,
  testID,
  webProps,
  nativeProps,
}: ProgressBarProps) {
  // CVA's inferred prop types admit `null`; normalize once (Badge pattern).
  const colour = colourProp ?? 'primary';
  const size = sizeProp ?? 'default';
  const clamped = Math.min(100, Math.max(0, Number.isFinite(value) ? value : 0));
  // Indeterminate is a linear-only mode; the other types ignore it.
  const isIndeterminate = indeterminate && type === 'linear';
  // The label text always derives from `value` — it can never drift from the
  // bar (Figma's "Label" axis is a pure Yes/No toggle). No value, no label.
  const label = showLabel && !isIndeterminate ? `${Math.round(clamped)}%` : undefined;

  const a11yProps = {
    accessibilityRole: 'progressbar',
    accessibilityLabel,
    testID,
    webProps: {
      'aria-valuemin': 0,
      'aria-valuemax': 100,
      'aria-valuenow': isIndeterminate ? undefined : clamped,
      // Running work is busy until it completes.
      'aria-busy': isIndeterminate || (live && clamped < 100) ? true : undefined,
      ...webProps,
    },
    nativeProps,
  } as const;

  const barLabel = label ? (
    <Text testID={subTestID(testID, 'label')} className={PROGRESS_BAR_LABEL_CLASS}>
      {label}
    </Text>
  ) : null;

  if (type === 'segmented') {
    const filledCount = Math.round((clamped / 100) * segments);
    return (
      <View
        {...a11yProps}
        className={cn('w-full flex-row items-center gap-2', className)}>
        <View className="min-w-0 flex-1 flex-row items-center gap-3">
          {Array.from({ length: segments }, (_, i) => (
            <View
              key={i}
              className={progressSegmentVariants({
                colour,
                filled: i < filledCount,
                dimmed: isProgressSegmentDimmed(i, highlightedSegment),
              })}
            />
          ))}
        </View>
        {barLabel}
      </View>
    );
  }

  if (type === 'circular' || type === 'semicircle') {
    return (
      <RingProgress
        variant={type}
        colour={colour}
        size={size}
        clamped={clamped}
        label={label}
        className={className}
        a11yProps={a11yProps}
      />
    );
  }

  // Linear (default) — the fill springs between values (progress-motion.ts):
  // translateX only, rAF on web, Reanimated on native; see AnimatedFill.
  return (
    <View {...a11yProps} className={cn('w-full flex-row items-center gap-2', className)}>
      <View className={cn(progressTrackVariants({ colour }), 'relative min-w-0 flex-1')}>
        <AnimatedFill
          fraction={clamped / 100}
          className={progressFillVariants({ colour })}
          live={live}
          indeterminate={isIndeterminate}
          sheen={sheen}
        />
      </View>
      {barLabel}
    </View>
  );
}
(ProgressBar as { displayName?: string }).displayName = 'ProgressBar';

function RingProgress({
  variant,
  colour,
  size,
  clamped,
  label,
  className,
  a11yProps,
}: {
  variant: 'circular' | 'semicircle';
  colour: NonNullable<ProgressBarProps['colour']>;
  size: NonNullable<ProgressBarProps['size']>;
  clamped: number;
  label: string | undefined;
  className: ProgressBarProps['className'];
  a11yProps: object & { testID?: string };
}) {
  const { isDark } = useTheme();
  const { box, stroke } = CIRCULAR_GEOMETRY_FOR[size];
  const { track, fill } = resolveProgressRingColors(colour, isDark);

  const radius = (box - stroke) / 2;
  const center = box / 2;
  const circumference = 2 * Math.PI * radius;

  // Circular = full 360° ring starting at 12 o'clock. Semicircle = an
  // open-bottom gauge: the track spans SEMICIRCLE_SWEEP_DEG with the gap
  // centred on 6 o'clock, and its start endpoint sits at the bottom-left.
  const isGauge = variant === 'semicircle';
  const sweepDeg = isGauge ? SEMICIRCLE_SWEEP_DEG : 360;
  const rotation = isGauge ? 90 + (360 - sweepDeg) / 2 : -90;
  const trackLen = (sweepDeg / 360) * circumference;
  const fillLen = (clamped / 100) * trackLen;
  const transform = `rotate(${rotation} ${center} ${center})`;

  const ring = (
    <Svg width={box} height={box} viewBox={`0 0 ${box} ${box}`} fill="none">
      {/* Track — a full circle for `circular`, an arc (dashed) for the gauge. */}
      <Circle
        cx={center}
        cy={center}
        r={radius}
        stroke={track}
        strokeWidth={stroke}
        {...(isGauge
          ? {
              strokeLinecap: 'round' as const,
              strokeDasharray: `${trackLen} ${circumference}`,
              transform,
            }
          : {})}
      />
      {clamped > 0 ? (
        // Fill sweeps clockwise from the start endpoint, with round caps —
        // matching the Figma ring exports.
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={fill}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${fillLen} ${circumference}`}
          transform={transform}
        />
      ) : null}
    </Svg>
  );

  const labelText = label ? (
    <Text
      testID={subTestID(a11yProps.testID, 'label')}
      className={progressCircularLabelVariants({ colour, ring: variant, size })}>
      {label}
    </Text>
  ) : null;

  // xsmall/small/default: label sits beside the ring (7px gap for circular,
  // 4px for the gauge per the Figma frames); medium/large/xlarge: centered
  // inside (the gauge's open centre reads as the natural place for the value).
  if (size === 'xsmall' || size === 'small' || size === 'default') {
    return (
      <View
        {...a11yProps}
        className={cn(
          'w-fit flex-row items-center',
          isGauge ? 'gap-1' : 'gap-[7px]',
          className,
        )}>
        {ring}
        {labelText}
      </View>
    );
  }

  return (
    <View {...a11yProps} className={cn('relative w-fit', className)}>
      {ring}
      {labelText ? (
        <View className="absolute inset-0 items-center justify-center">{labelText}</View>
      ) : null}
    </View>
  );
}
